import React, { useState } from "react";
import { Box, Chip, Typography } from "@mui/material";
import currency from "currency.js";
import {
  getItemBasePrice,
  getItemPrice,
  getAmountWithTxnFee,
  getItemSurchargeTotal,
} from "../../util";
import PosQuantityDialog from "./PosQuantityDialog";

export function lineItemWithStandardQuantity(item, quantity) {
  const next = { ...item, quantity: +quantity };
  const price = getItemBasePrice(next.variant);
  const priceWithSurcharge = getItemPrice(next.variant, price);
  const total = currency(priceWithSurcharge).multiply(next.quantity).value;
  return { ...next, price, total };
}

export function lineItemWithCustomQuantity(item, quantity, user) {
  const q = +quantity;
  const priceWithFees = item.includeFees
    ? getAmountWithTxnFee(item.price, user.config?.fees)
    : item.price;
  const surcharge = getItemSurchargeTotal(item.variant, item.price);
  const total = currency(priceWithFees).add(surcharge).multiply(q).value;
  return { ...item, quantity: q, total };
}

/** Title, variants & price of a POS cart row. Wrapped by StandardItem/CustomItem so a tap opens the item editor. */
export function PosCartLineItemDetails(props) {
  const { item } = props;

  const otherVariants = item.variant
    ? Object.keys(item.variant).filter((o) => o !== "size" && o !== "flavor").length
    : 0;

  return (
    <Box component="span" sx={{ display: "flex", gap: "5px" }}>
      <Box component="span" sx={{ display: "block", flexGrow: "1" }}>
        <Typography component="span" sx={{ display: "block", fontSize: "14px" }}>
          {item.title}
        </Typography>
        <Box component="span" sx={{ display: "block" }}>
          {item.variant?.size && (
            <Typography component="span" sx={{ display: "block", fontSize: "12px" }}>
              {item.variant.size.name}
            </Typography>
          )}
          {item.variant?.flavor && (
            <Typography component="span" sx={{ display: "block", fontSize: "12px" }}>
              {item.variant.flavor.name}
            </Typography>
          )}
          {otherVariants > 0 && (
            <Typography component="span" sx={{ display: "block", fontSize: "12px" }}>
              +{otherVariants} MORE
            </Typography>
          )}
        </Box>
      </Box>
      <Box component="span">
        <Typography component="span" variant="overline">
          {currency(item.total || item.price).format()}
        </Typography>
      </Box>
    </Box>
  );
}

export default function PosCartLineItem(props) {
  const { item, user, onQuantityCommit, onDelete } = props;

  const [qtyOpen, setQtyOpen] = useState(false);

  const applyQuantityChange = (rawVal) => {
    const q = +rawVal;
    if (!(q > 0)) {
      return;
    }

    onQuantityCommit(
      item.productId
        ? lineItemWithStandardQuantity(item, q)
        : lineItemWithCustomQuantity(item, q, user)
    );
    setQtyOpen(false);
  };

  const handleRemove = () => {
    setQtyOpen(false);
    onDelete();
  };

  return (
    <Box sx={{ display: "flex", gap: "5px" }}>
      <Box>
        <Chip
          size="small"
          label={item.quantity}
          onClick={() => setQtyOpen(true)}
          sx={{ minWidth: "3rem", cursor: "pointer" }}
        />
      </Box>
      <Box
        sx={{
          flexGrow: "1",
          minWidth: 0,
          // StandardItem/CustomItem wrap the details in an inline span/Link, which would
          // otherwise shrink to the text and leave the rest of the row untappable
          "& > *": { display: "block", width: "100%", minWidth: 0 },
        }}
      >
        {props.children}
      </Box>
      <PosQuantityDialog
        open={qtyOpen}
        title={item.title}
        quantity={item.quantity}
        onClose={() => setQtyOpen(false)}
        onUpdate={applyQuantityChange}
        onRemove={handleRemove}
      />
    </Box>
  );
}
